/**
 * FleetManager V3 - Resource Alerts Widget
 * Fluids & wear items alerts across the fleet - clickable to navigate to vehicles.
 *
 * Two-section layout:
 *  - "À traiter" — critical/warning resources NOT yet covered by a planned MR.
 *  - "En cours"  — resources of vehicles that already have a planned MR;
 *                  collapsible, closed by default. Workaround coarseness:
 *                  atténuation is at vehicle granularity (one planned MR
 *                  silences all that vehicle's alerts). Tracked as UX debt
 *                  in tech_debt_alert_resource_link.md until we add a
 *                  resource-to-MR link post-salon.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormat } from '../../../../i18n/format.js';
import { Droplets, ChevronDown, ChevronRight, AlertTriangle, Wrench } from 'lucide-react';
import { Card } from '../../shared/index.js';
import { Badge } from '../../shared/index.js';
import { vehicleResourceAPI } from '../../../../api/vehicles.js';
import { maintenanceAPI } from '../../../../api/maintenance.js';

// Local YYYY-MM-DD (avoids UTC drift from toISOString in non-UTC zones).
function todayYMD() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// A planned MR still "covers" the alert until its scheduled date passes.
// On J+1 with status still planned, we re-surface the red badge so a missed
// appointment doesn't keep the vehicle silenced forever.
function isCoveringNow(mr) {
  if (!mr || mr.status !== 'planned') return false;
  if (!mr.scheduled_date) return true; // planned but no date yet → still counts
  return mr.scheduled_date >= todayYMD();
}

// Libellés des ressources : dashboard.resources.labels.<resource_type>
// (fluides, pièces d'usure, état général — miroir de WEAR_TYPES). Un code
// inconnu est affiché tel quel (repli defaultValue) : c'est un rapport de bug.
function resourceLabel(t, type) {
  return t(`dashboard.resources.labels.${type}`, { defaultValue: type });
}

const WEAR_TYPES = new Set([
  'brake_pad_front', 'brake_pad_rear',
  'tire_front_left', 'tire_front_right', 'tire_rear_left', 'tire_rear_right',
  'windshield', 'body_condition', 'headlights', 'taillights', 'mirrors',
]);

function ActiveAlertItem({ alert, isLast, onNavigate }) {
  const { t } = useTranslation();
  const isCritical = alert.status === 'critical' || alert.status === 'replace';
  const iconBg = isCritical
    ? 'bg-red-100 dark:bg-red-500/20'
    : alert.status === 'warning'
      ? 'bg-amber-100 dark:bg-amber-500/20'
      : 'bg-yellow-100 dark:bg-yellow-500/20';
  const iconColor = isCritical
    ? 'text-red-500 dark:text-red-400'
    : alert.status === 'warning'
      ? 'text-amber-500 dark:text-amber-400'
      : 'text-yellow-500 dark:text-yellow-400';

  return (
    <div
      onClick={() => onNavigate?.('vehicles', { selectedId: alert.vehicle_id })}
      className={`flex items-center gap-2 p-2.5 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group ${!isLast ? 'border-b border-gray-100 dark:border-slate-700/30' : ''}`}
    >
      <div className={`p-1.5 rounded-lg ${iconBg}`}>
        <AlertTriangle size={14} className={iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-900 dark:text-white truncate">
            {alert.plate || t('common.states.notAvailable')} — {resourceLabel(t, alert.resource_type)}
          </span>
          <Badge variant={isCritical ? 'critical' : 'warning'}>
            {alert.value_percent != null
              ? WEAR_TYPES.has(alert.resource_type)
                ? t('dashboard.resources.wear', { percent: 100 - alert.value_percent })
                : `${alert.value_percent}%`
              : '—'}
          </Badge>
        </div>
        <div className="text-xs text-gray-500 dark:text-slate-400 truncate">
          {alert.vehicle_label || ''}
        </div>
      </div>
      <ChevronRight size={12} className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

function CoveredAlertItem({ alert, isLast, onNavigate, plannedMR }) {
  const { t, fmt } = useFormat();
  const appointment = plannedMR?.scheduled_date
    ? (plannedMR.garage_name
      ? t('dashboard.resources.appointmentAt', { date: fmt.date(plannedMR.scheduled_date, { day: '2-digit', month: '2-digit' }), garage: plannedMR.garage_name })
      : t('dashboard.resources.appointment', { date: fmt.date(plannedMR.scheduled_date, { day: '2-digit', month: '2-digit' }) }))
    : t('dashboard.resources.plannedIntervention');
  return (
    <div
      onClick={() => onNavigate?.('vehicles', { selectedId: alert.vehicle_id })}
      className={`flex items-center gap-2 p-2.5 hover:bg-orange-50/40 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group ${!isLast ? 'border-b border-orange-100/60 dark:border-slate-700/30' : ''}`}
    >
      <div className="p-1.5 rounded-lg bg-orange-100 dark:bg-orange-500/20">
        <Wrench size={14} className="text-orange-500 dark:text-orange-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-slate-300 truncate">
            {alert.plate || t('common.states.notAvailable')} — {resourceLabel(t, alert.resource_type)}
          </span>
          <Badge variant="warning">{t('dashboard.resources.planned')}</Badge>
        </div>
        <div className="text-xs text-orange-600 dark:text-orange-400 truncate">
          {appointment}
        </div>
      </div>
      <ChevronRight size={12} className="text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}

export function ResourceAlertsWidget({ onNavigate }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isCoveredExpanded, setIsCoveredExpanded] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [vehiclesWithAlerts, setVehiclesWithAlerts] = useState(0);
  // Map<vehicle_id, { id, scheduled_date, garage_name }> — earliest planned MR per vehicle.
  const [plannedByVehicle, setPlannedByVehicle] = useState(new Map());

  useEffect(() => {
    vehicleResourceAPI.getAlertsSummary()
      .then(res => {
        if (!res?.data) return;
        const flat = [];
        (res.data.by_vehicle || []).forEach(vEntry => {
          (vEntry.alerts || []).forEach(a => {
            flat.push({
              ...a,
              vehicle_id: vEntry.vehicle_id,
              plate: vEntry.license_plate || vEntry.plate || 'N/A',
              vehicle_label: vEntry.make && vEntry.model ? `${vEntry.make} ${vEntry.model}` : '',
            });
          });
        });
        const order = { critical: 0, replace: 0, warning: 1, low: 2 };
        flat.sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3));
        setAlerts(flat);
        setVehiclesWithAlerts(res.data.vehicles_with_alerts || 0);
      })
      .catch(() => {});

    maintenanceAPI.getAll()
      .then(res => {
        const list = res?.data || [];
        const map = new Map();
        for (const m of list) {
          if (!m.vehicle_id) continue;
          if (!isCoveringNow(m)) continue;
          const prev = map.get(m.vehicle_id);
          if (!prev || (m.scheduled_date && (!prev.scheduled_date || m.scheduled_date < prev.scheduled_date))) {
            map.set(m.vehicle_id, {
              id: m.id,
              scheduled_date: m.scheduled_date,
              garage_name: m.garage_name || null,
            });
          }
        }
        setPlannedByVehicle(map);
      })
      .catch(() => {});
  }, []);

  const activeAlerts = alerts.filter(a => !plannedByVehicle.has(a.vehicle_id));
  const coveredAlerts = alerts.filter(a => plannedByVehicle.has(a.vehicle_id));
  const criticalCount = activeAlerts.filter(a => a.status === 'critical' || a.status === 'replace').length;
  const hasCritical = criticalCount > 0;

  return (
    <Card padding="p-0" className="overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${hasCritical ? 'bg-red-100 dark:bg-red-500/20' : 'bg-cyan-100 dark:bg-cyan-500/20'}`}>
            <Droplets size={16} className={hasCritical ? 'text-red-500 dark:text-red-400' : 'text-cyan-500 dark:text-cyan-400'} />
          </div>
          <div>
            <div className="font-semibold text-gray-900 dark:text-white text-sm">{t('dashboard.resources.title')}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400">
              {t(coveredAlerts.length > 0 ? 'dashboard.resources.summaryWithProgress' : 'dashboard.resources.summary', {
                vehicles: t('dashboard.resources.vehicles', { count: vehiclesWithAlerts }),
                toHandle: t('dashboard.resources.toHandle', { count: criticalCount }),
                inProgress: t('dashboard.resources.inProgress', { count: coveredAlerts.length })
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasCritical && (
            <Badge variant="critical">{criticalCount}</Badge>
          )}
          {isExpanded ? (
            <ChevronDown size={16} className="text-gray-400 dark:text-slate-400" />
          ) : (
            <ChevronRight size={16} className="text-gray-400 dark:text-slate-400" />
          )}
        </div>
      </div>

      {/* Section 1 — À traiter */}
      {isExpanded && activeAlerts.length > 0 && (
        <div
          className="border-t border-gray-100 dark:border-slate-700/50 overflow-y-auto"
          style={{ maxHeight: activeAlerts.length > 6 ? '320px' : 'none' }}
        >
          {activeAlerts.map((alert, index) => (
            <ActiveAlertItem
              key={`active-${alert.vehicle_id}-${alert.resource_type}`}
              alert={alert}
              isLast={index === activeAlerts.length - 1}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}

      {/* Section 2 — En cours (collapsible, closed by default) */}
      {isExpanded && coveredAlerts.length > 0 && (
        <div className="border-t border-gray-100 dark:border-slate-700/50">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIsCoveredExpanded(v => !v); }}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-orange-700 dark:text-orange-300 bg-orange-50/40 dark:bg-orange-900/10 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
          >
            <span className="flex items-center gap-2">
              <Wrench size={12} className="text-orange-500" />
              {t('dashboard.resources.plannedCount', { count: coveredAlerts.length })}
            </span>
            {isCoveredExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          {isCoveredExpanded && (
            <div className="overflow-y-auto" style={{ maxHeight: coveredAlerts.length > 6 ? '240px' : 'none' }}>
              {coveredAlerts.map((alert, index) => (
                <CoveredAlertItem
                  key={`covered-${alert.vehicle_id}-${alert.resource_type}`}
                  alert={alert}
                  isLast={index === coveredAlerts.length - 1}
                  onNavigate={onNavigate}
                  plannedMR={plannedByVehicle.get(alert.vehicle_id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty state — only when nothing at all */}
      {isExpanded && alerts.length === 0 && (
        <div className="border-t border-gray-100 dark:border-slate-700/50 p-4 text-center">
          <div className="text-emerald-600 dark:text-emerald-400 text-sm">{t('dashboard.resources.allOk')}</div>
          <div className="text-xs text-gray-400 dark:text-slate-500">{t('dashboard.resources.allOkHint')}</div>
        </div>
      )}

      {/* All-covered state — every alert has a planned MR (no active items) */}
      {isExpanded && activeAlerts.length === 0 && coveredAlerts.length > 0 && (
        <div className="border-t border-gray-100 dark:border-slate-700/50 p-3 text-center">
          <div className="text-emerald-600 dark:text-emerald-400 text-sm">{t('dashboard.resources.underControl')}</div>
          <div className="text-xs text-gray-400 dark:text-slate-500">{t('dashboard.resources.underControlHint')}</div>
        </div>
      )}
    </Card>
  );
}

export default ResourceAlertsWidget;
